/**
 * Shared types for the pure simulation core. This module (and everything under
 * src/sim, src/core, src/players, src/telemetry/schema) must stay free of DOM
 * and Babylon imports so the sim runs headless under Vitest/Node.
 */

import type { MapDoc } from "./map/types";

export type Variant = "A" | "B" | "C";
export type LevelId = "cal1" | "cal2" | "cal3" | "research" | "mission";
export type SafetyState = "NORMAL" | "SLOWDOWN" | "EMERGENCY_STOP";
export type Phase = "approach" | "cue" | "negotiation" | "completion";
export type Side = "left" | "right";

export type OutcomeResult =
  | "pass_left"
  | "pass_right"
  | "yield"
  | "completed" // calibration levels only
  | "collision"
  | "timeout"
  | "incomplete";

export type EventType =
  | "level_start"
  | "cue_onset"
  | "research_window_end"
  | "first_joystick_input"
  | "first_lateral_input"
  | "first_commitment"
  | "direction_reversal"
  | "joystick_release"
  | "safety_slowdown"
  | "emergency_stop"
  | "collision"
  | "kerb_climb"
  | "rough_ride"
  | "fall_in"
  | "adrift"
  | "wrong_side"
  | "passing_moment"
  | "level_complete"
  | "retry"
  | "timeout"
  | "stuck_detected"
  | "recovery_started"
  | "recovery_complete"
  | "safe_pose_restore"
  | "encounter_start"
  | "encounter_end"
  // Living Safe Corridor (ADR-065).
  | "corridor_shortened"
  | "pocket_created"
  | "pocket_entered"
  | "yield_success"
  | "premature_resume"
  | "amber_cleared"
  | "resume_began"
  | "return_right_completed"
  | "lane_star"
  | "feedback"
  | "abrupt_agent"
  | "guidance_cue"
  | "wrong_way"
  | "back_on_route";

export type RecoveryMode = "normal" | "reverse" | "align";

/** A dynamic hazard the safety governor must respect (agents, pedestrians). */
export interface SafetyHazard {
  x: number;
  z: number;
  /** Sway-free intent velocity. */
  safetyVx: number;
  safetyVz: number;
  radius: number;
  id: string;
}

/** Mission delivery score, computed once at completion (never shown live). */
export interface MissionScore {
  completed: boolean;
  completion_time_s: number;
  emergency_stops: number;
  recovery_events: number;
  collisions: number;
  /** Rapid back-and-forth side changes (dithering), NOT every lane change:
   * a reversal within OSCILLATION_WINDOW_S of the previous side change. */
  direction_oscillations: number;
  /** 0..100 — package condition after wobbles/hits. */
  package_stability: number;
  /** 0..100 — derived from stops/recoveries/oscillation. */
  smoothness: number;
  /** How often other agents were forced into abrupt movement. */
  abrupt_agent_events: number;
  /** Times the robot left the delivery route (wrong branch / wrong way). */
  wrong_way_events: number;
  /** 0..100 composite. */
  total: number;
}

/** Turn-by-turn guidance shown in the top strip (mission only, ADR-022). */
export interface Guidance {
  kind: "straight" | "left" | "right" | "roundabout" | "arrive" | "turn_around";
  /** Distance to the maneuver (m); null when turning around. */
  distance: number | null;
  /** Roundabout exit number (1-based). */
  exit: number | null;
  label: string;
}

export interface RobotProfile {
  _status: string;
  width_m: number;
  length_m: number;
  nominal_speed_mps: number;
  max_speed_mps: number;
  max_accel_mps2: number;
  max_decel_mps2: number;
  max_yaw_rate_radps: number;
  control_latency_ms: number;
  safety_ttc_s: number;
}

/**
 * One joystick command in screen space: dirY = up = forward (+Z),
 * dirX = right (+X). mag in [0, 1]; mag below the deadzone means released.
 */
export interface JoystickCommand {
  dirX: number;
  dirY: number;
  mag: number;
}

export const NEUTRAL_COMMAND: JoystickCommand = Object.freeze({
  dirX: 0,
  dirY: 0,
  mag: 0,
});

export interface RobotState {
  x: number;
  z: number;
  /** Radians from +Z toward +X. 0 = toward destination. */
  heading: number;
  /** m/s along heading; never negative under player control (the automatic
   * recovery maneuver may briefly hold a small negative value while backing up). */
  speed: number;
  /** rad/s. */
  yawRate: number;
}

export interface PedestrianState {
  x: number;
  z: number;
  vx: number;
  vz: number;
  /** Intent velocity with the gait sway removed — used by the robot's safety
   * TTC so the ±0.3 m/s sway oscillation (which reverses within ~0.14 s)
   * cannot masquerade as a sustained collision course. */
  safetyVx: number;
  safetyVz: number;
  active: boolean;
}

/** Axis-aligned box obstacle in the alley. */
export interface Obstacle {
  x: number;
  z: number;
  halfW: number;
  halfD: number;
  /** Render height hint (m); irrelevant to 2D sim collisions. */
  height: number;
  /** Render kind hint ("parked_car", "barrier", ...); irrelevant to the sim. */
  kind?: string;
  /** A hole in the road rather than a thing on it (ADR-043). */
  pit?: boolean;
  /** A pit no driver sees coming: scripted players must not avoid it. */
  unmarked?: boolean;
  /** A ramp driven over, never into: exempt from collision entirely (ADR-045). */
  bump?: boolean;
}

export interface WorldDef {
  /** Walls at x = +/- alleyHalfWidth (ignored when `route` is set). */
  alleyHalfWidth: number;
  alleyLength: number;
  destination: { x: number; z: number };
  destinationRadius: number;
  /** Crossing the destination line (z >= dest z) within this |x - dest.x| completes. */
  destinationLineHalfWidth: number;
  /**
   * Path-progress intervals where the route crosses a road like a pedestrian
   * (the zebra leg of a two-stage left turn): the corridor and the painted
   * lane centre themselves on the path there instead of keeping right.
   */
  crossings?: ReadonlyArray<{ s0: number; s1: number }>;
  obstacles: Obstacle[];
  /**
   * Obstacles no taller than this (m) are driven over rather than into
   * (ADR-038). 0 — the default — makes every obstacle solid, which is the
   * assisted model the lab levels were recorded against. The simulation sets
   * it from the attempt's assist mode.
   */
  rideOverBelow?: number;
  /** Mission: curbs are the road network's boundary (ADR-022). */
  network?: import("./network").RoadNetwork;
  /** Mission: the delivery path providing the (s, d) frame. */
  path?: import("./path").DeliveryPath;
}

export interface ScenarioConfig {
  level: LevelId;
  variant: Variant;
  mirror: boolean;
  seed: number;
  /** Speed profile name; defaults to the game default ("fast") when omitted. */
  speedProfile?: string;
  /** Player tuning (Tweak panel); defaults to 1.0 / 1.0 when omitted. */
  tuning?: Tuning;
  /** Control model; defaults to the level's own (rate on the mission). */
  controlScheme?: ControlScheme;
  /**
   * Living Safe Corridor override (ADR-065). Defaults to the level's own
   * feature flag. Exists so behavioral tests can exercise the fallback
   * safety net beneath the corridor, and as an A/B lever for the study.
   */
  corridor?: boolean;
  /**
   * A custom map to play instead of the built-in district (map editor).
   * Compiled by src/sim/map/compile.ts; the district stays untouched code.
   */
  map?: MapDoc;
  /**
   * Free roam (ADR-128): no errand, so the route is a corridor to look at
   * rather than a trip to complete. The one thing the level still takes from
   * it is where to put the robot - see buildLevel.
   */
  freeRoam?: boolean;
  /**
   * Which catalogued situation this run exercises (RARE_CASE_CATALOG_EN.md,
   * e.g. "SS-PED-01"). Recorded in the episode so demonstrations group by
   * case. Falls back to the map's own `case_id`.
   */
  caseId?: string;
  /**
   * Driving assistance model.
   *
   * `assisted` is the original two-stage governor: it slows for a hazard it
   * has not touched and stops before contact. `real` removes that — nothing
   * brakes for the robot, and it stops when it actually hits something, which
   * is how the hardware behaves and what the collision metrics are supposed to
   * be counting. Defaults to `real` on the mission and on custom maps, and to
   * `assisted` on the lab levels, whose trajectories are pinned.
   */
  assist?: AssistMode;
}

/** Control model for the joystick (ADR-023). `heading` is the ADR-017 model
 * (the stick points where you want to go); `rate` is the racing model (the
 * stick is a steering wheel and a throttle). Recorded with every episode. */
export type ControlScheme = "heading" | "rate";

/** Player-adjustable tuning, clamped to the ranges in constants.ts. */
export interface Tuning {
  /** Steering sensitivity multiplier (0.5 .. 2.0). */
  sensitivity: number;
  /** Speed multiplier on top of the speed profile (0.25 .. 1.5). */
  speedScale: number;
}

export interface SimEvent {
  t: number;
  type: EventType;
  data?: Record<string, unknown>;
}

/** One 20 Hz telemetry sample. variant/mirror/seed are constant but repeated by requirement. */
export interface SimSample {
  t: number;
  robot_pos: [number, number];
  robot_heading: number;
  robot_speed: number;
  robot_yaw_rate: number;
  /** Delivery-path frame [progress s, lateral offset d] (lab levels: [z, x]). */
  robot_path: [number, number];
  ped_pos: [number, number] | null;
  ped_vel: [number, number] | null;
  joy_dir: [number, number];
  joy_mag: number;
  safety_state: SafetyState;
  phase: Phase;
  variant: Variant;
  mirror: 0 | 1;
  seed: number;
  // --- Living Safe Corridor (ADR-065), corridor-enabled levels only. ------
  // Optional and ABSENT on lab levels: the pinned lab trajectory hashes
  // serialize samples verbatim, so these must not exist there at all.
  corridor_state?: string;
  corridor_len?: number;
  corridor_width?: number;
  corridor_center_d?: number;
  /** [s0, s1] of the waiting pocket, or null when there is none. */
  corridor_pocket?: [number, number] | null;
  /** Road user whose amber path is showing, or null. */
  amber_id?: string | null;
  /** The automatic speed limit granted this sample (corridor ∧ governor). */
  speed_cap?: number;
  /** Path distance to the binding conflict, or null. */
  conflict_dist?: number | null;
  priority_id?: string | null;
  /** 1 while the robot body is inside the blue band. */
  in_corridor?: 0 | 1;
  // --- Driver-interface fields (ADR-075), mission levels only. ----------
  /**
   * The commanded Twist `[v, w]` this step (m/s, rad/s) — what the controller
   * asked the plant for, the analogue of the real robot's
   * `joystick_vel_cmd`. Distinct from `robot_speed`/`robot_yaw_rate`, which
   * are what the plant then did.
   */
  cmd?: [number, number];
  /**
   * Every other road user within 60 m, as
   * `[id, type, x, z, vx, vz, heading, radius]`. Without this a policy
   * learned from the samples cannot see the traffic it was reacting to.
   */
  agents?: Array<[string, string, number, number, number, number, number, number]>;
}

/** See ScenarioConfig.assist. */
export type AssistMode = "assisted" | "real";

export interface Outcome {
  result: OutcomeResult;
  passing_moment_t: number | null;
  committed_side: Side | null;
  collision: boolean;
  completed: boolean;
  duration_s: number;
}

/** A visible agent, as exposed to scripted players. */
export interface AgentView {
  id: string;
  type: string;
  x: number;
  z: number;
  vx: number;
  vz: number;
  radius: number;
  moving: boolean;
  heading: number;
}

/** Read-only view handed to scripted players each step. */
export interface SimView {
  t: number;
  phase: Phase;
  robot: Readonly<RobotState>;
  ped: Readonly<PedestrianState> | null;
  cueOnsetSeen: boolean;
  /** Sign of the pedestrian's scripted drift as observable on screen, once discernible. */
  pedDriftSide: Side | null;
  destination: { x: number; z: number };
  /** Completed automatic recoveries (reverse maneuvers + safe-pose restores). */
  recoveryCount: number;
  /** Mission levels: active collidable agents, path frame, distance left. */
  agents: readonly AgentView[];
  /** Static collidable props (parked vehicles, barriers, cones). */
  obstacles: readonly Obstacle[];
  /** The delivery path (mission) or null. */
  path: import("./path").DeliveryPath | null;
  /** Road network (mission) or null. */
  network: import("./network").RoadNetwork | null;
  /** Robot's path frame (mission); lab levels report s = z, d = x. */
  progress: { s: number; d: number };
  /** Control-frame yaw: joystick "up" drives along this heading. */
  frameYaw: number;
  /** Which control model the stick is being read as (ADR-023). */
  controlScheme: ControlScheme;
  guidance: Guidance | null;
  distanceToGo: number;
  /** Living Safe Corridor plan (corridor-enabled levels), or null. */
  corridor: import("./corridor").CorridorPlan | null;
}

/** Research-sample validity bookkeeping (research level only; null otherwise). */
export interface ResearchValidity {
  /** False when a recovery occurred before first commitment (primary sample invalid). */
  sample_valid: boolean;
  invalid_reason: string | null;
  /** True when a recovery occurred after first commitment (initial decision retained). */
  post_commit_recovery: boolean;
}
